import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { api, extractApiErrorMessage } from '../../../../../../api/client';

// ── Recording Quality Config ──
const REC_FPS_IDEAL = 15;
const REC_FPS_MAX = 20;
const REC_VIDEO_BITRATE = 1_500_000;   // 1.5 Mbps — suficiente para screencast de treinamento
const REC_AUDIO_BITRATE = 64_000;      // 64 kbps — fala narrada, mono
const REC_CHUNK_INTERVAL_MS = 30_000;  // Flush chunk a cada 30 segundos

export function useEcadMediaRecorder(masterToken: string) {
  const [status, setStatus] = useState<'idle' | 'initializing' | 'countdown' | 'recording' | 'paused' | 'completed'>('idle');
  const [useMic, setUseMic] = useState(true);
  const [timer, setTimer] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [countdownVal, setCountdownVal] = useState(3);

  // Media Refs
  const recorderRef = useRef<MediaRecorder | null>(null);
  const audioRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const timerIntervalRef = useRef<any>(null);
  const statusRef = useRef(status);
  const countdownIntervalRef = useRef<any>(null);

  // ── Chunked Recording Session ──
  const recordingSessionRef = useRef<string>('');
  const videoChunkIndexRef = useRef<number>(0);
  const audioChunkIndexRef = useRef<number>(0);
  const chunkUploadQueueRef = useRef<Promise<void>[]>([]);
  const videoChunkBlobsRef = useRef<Blob[]>([]); // Mantém blobs para ObjectURL de preview

  // ── Kanban Relay URL — captura na inicialização antes de replaceState ──
  const kanbanRelayUrlRef = useRef<string | null>(
    new URLSearchParams(window.location.search).get('kanban_relay')
  );

  // Real-time recording size tracking (for FAB overlay)
  const recordingSizeBytesRef = useRef(0);

  // Final Video File State
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [uploading, setUploading] = useState(false);

  // AI & Voice Processing States
  const [videoScript, setVideoScript] = useState('');
  const [legendaUrl, setLegendaUrl] = useState('');
  const [gcsVideoPath, setGcsVideoPath] = useState('');
  const [processedVideoUrl, setProcessedVideoUrl] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const [isSynthesizing, setIsSynthesizing] = useState(false);
  const [voiceName, setVoiceName] = useState(() => localStorage.getItem('ecad_last_voice') || 'Kore');
  const [isTestingVoice, setIsTestingVoice] = useState(false);
  const [transcriptionSegments, setTranscriptionSegments] = useState<Array<{ index: number; start: string; end: string; text: string; originalText?: string; resolvedAddedSec?: number }>>([]);

  // ── Versionamento de Aprimoramento ──
  type RefinementVersion = {
    label: string;
    segments: Array<{ index: number; start: string; end: string; text: string; resolvedAddedSec?: number }>;
    timestamp: number;
    analysis?: string;
  };
  const [originalSegments, setOriginalSegments] = useState<Array<{ index: number; start: string; end: string; text: string; resolvedAddedSec?: number }>>([]); 
  const [originalVideoScript, setOriginalVideoScript] = useState('');
  const [refinementVersions, setRefinementVersions] = useState<RefinementVersion[]>([]);
  const [activeVersionIndex, setActiveVersionIndex] = useState(-1); // -1 = original

  const timelineRef = useRef<any[]>([]);

  // Update statusRef
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  // ── Restaurar sessão do sessionStorage (sobrevive a reload) ──
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('ecad_session');
      if (!saved) return;
      const session = JSON.parse(saved);

      // Validar que a sessão não é muito antiga (max 2h)
      if (session.timestamp && Date.now() - session.timestamp > 2 * 60 * 60 * 1000) {
        sessionStorage.removeItem('ecad_session');
        console.log('[ECAD] 🗑️ Sessão expirada removida do sessionStorage');
        return;
      }

      console.log('[ECAD] 🔄 Restaurando sessão do sessionStorage:', session.sessionId);

      // Restaurar vídeo (server URL — não morre em reload)
      if (session.videoUrl) {
        setPreviewUrl(session.videoUrl);
        console.log('[ECAD] ✅ Preview URL restaurado:', session.videoUrl);
      }
      if (session.gcsVideoPath) {
        setGcsVideoPath(session.gcsVideoPath);
      }
      if (session.audioUrl) {
        (window as any).__ecadLastAudioUrl = session.audioUrl;
      }
      if (session.sessionId) {
        recordingSessionRef.current = session.sessionId;
      }

      // Restaurar transcrição
      if (session.segments && session.segments.length > 0) {
        const mapped = session.segments.map((seg: any) => ({
          ...seg,
          originalText: seg.originalText || seg.text,
        }));
        setTranscriptionSegments(mapped);
        setOriginalSegments(JSON.parse(JSON.stringify(mapped)));
        console.log(`[ECAD] ✅ ${mapped.length} segmentos restaurados`);
      }
      if (session.videoScript) {
        setVideoScript(session.videoScript);
        setOriginalVideoScript(session.videoScript);
      }
      if (session.legendaUrl) {
        setLegendaUrl(session.legendaUrl);
      }

      // Marcar como completed (já tem vídeo e transcrição)
      setStatus('completed');
    } catch (e) {
      console.warn('[ECAD] Falha ao restaurar sessão:', e);
    }
   
  }, []);

  // Timer interval control
  useEffect(() => {
    if (status === 'recording') {
      timerIntervalRef.current = setInterval(() => {
        setTimer((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [status]);

  // Format timer seconds into HH:MM:SS
  const formatTime = (secs: number) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return [
      h > 0 ? String(h).padStart(2, '0') : null,
      String(m).padStart(2, '0'),
      String(s).padStart(2, '0')
    ].filter(Boolean).join(':');
  };

  // Play Beep sounds
  const playBeep = (type: 'start' | 'pause' | 'stop' | 'click') => {
    try {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      if (type === 'start') {
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(880, audioCtx.currentTime);
        gainNode.gain.setValueAtTime(0.05, audioCtx.currentTime);
        oscillator.start();
        oscillator.stop(audioCtx.currentTime + 0.12);
        
        setTimeout(() => {
          try {
            const audioCtx2 = new (window.AudioContext || (window as any).webkitAudioContext)();
            const osc2 = audioCtx2.createOscillator();
            const gain2 = audioCtx2.createGain();
            osc2.connect(gain2);
            gain2.connect(audioCtx2.destination);
            osc2.type = 'sine';
            osc2.frequency.setValueAtTime(1046.5, audioCtx2.currentTime);
            gain2.gain.setValueAtTime(0.05, audioCtx2.currentTime);
            osc2.start();
            osc2.stop(audioCtx2.currentTime + 0.18);
          } catch (e) {}
        }, 150);
      } else if (type === 'pause') {
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(587.33, audioCtx.currentTime);
        gainNode.gain.setValueAtTime(0.05, audioCtx.currentTime);
        oscillator.start();
        oscillator.stop(audioCtx.currentTime + 0.18);
      } else if (type === 'stop') {
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(659.25, audioCtx.currentTime);
        gainNode.gain.setValueAtTime(0.05, audioCtx.currentTime);
        oscillator.start();
        oscillator.stop(audioCtx.currentTime + 0.12);
        
        setTimeout(() => {
          try {
            const audioCtx2 = new (window.AudioContext || (window as any).webkitAudioContext)();
            const osc2 = audioCtx2.createOscillator();
            const gain2 = audioCtx2.createGain();
            osc2.connect(gain2);
            gain2.connect(audioCtx2.destination);
            osc2.type = 'sine';
            osc2.frequency.setValueAtTime(440, audioCtx2.currentTime);
            gain2.gain.setValueAtTime(0.05, audioCtx2.currentTime);
            osc2.start();
            osc2.stop(audioCtx2.currentTime + 0.18);
          } catch (e) {}
        }, 150);
      } else if (type === 'click') {
        oscillator.type = 'triangle';
        oscillator.frequency.setValueAtTime(1200, audioCtx.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(100, audioCtx.currentTime + 0.05);
        
        gainNode.gain.setValueAtTime(0.04, audioCtx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.05);
        
        oscillator.start();
        oscillator.stop(audioCtx.currentTime + 0.05);
      }
    } catch (err) {
      console.warn('Web Audio API beep blocked or unsupported:', err);
    }
  };

  // Ref for test voice audio cleanup
  const testVoiceAudioRef = useRef<HTMLAudioElement | null>(null);

  const handleStart = async (onStopCallback?: () => void) => {
    // ── Guard: prevent double-start / overlap ──
    if (status !== 'idle' && status !== 'completed') {
      console.warn('[ECAD] handleStart bloqueado — status atual:', status);
      return;
    }

    // Solicitar tela cheia imediatamente no gesto de clique do usuário
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      }
    } catch (fErr) {
      console.warn('[ECAD] Falha ao solicitar tela cheia no clique:', fErr);
    }

    // ── Cleanup: release any previous recording resources ──
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch (_) {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(t => t.stop());
      micStreamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    // Revoke previous ObjectURL to free memory
    if (previewUrl) {
      try { URL.revokeObjectURL(previewUrl); } catch (_) {}
    }
    recorderRef.current = null;
    audioRecorderRef.current = null;

    try {
      setErrorMsg('');
      setStatus('initializing');
      timelineRef.current = [];
      setVideoScript('');
      setLegendaUrl('');
      setGcsVideoPath('');
      recordingSizeBytesRef.current = 0;

      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'browser',
          frameRate: { ideal: REC_FPS_IDEAL, max: REC_FPS_MAX },
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true
        },
        preferCurrentTab: true,
        selfBrowserSurface: 'include',
        systemAudio: 'include'
      } as any);

      // Re-solicitar tela cheia com um pequeno delay para garantir que o Chrome
      // finalize a transição de saída automática causada pelo picker de mídia
      setTimeout(async () => {
        try {
          if (!document.fullscreenElement) {
            await document.documentElement.requestFullscreen();
          }
        } catch (fErr) {
          console.warn('[ECAD] Falha ao re-solicitar tela cheia após obter display stream:', fErr);
        }
      }, 200);

      // ── Configurar microfone ──
      let micStream: MediaStream | null = null;
      if (useMic) {
        try {
          micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch (err) {
          console.warn('Microfone negado ou não encontrado, gravando apenas tela.', err);
        }
      }

      // ── Combinar audio tracks (screen + mic) para gravação de áudio separada ──
      const screenAudioTracks = screenStream.getAudioTracks();
      const micAudioTracks = micStream ? micStream.getAudioTracks() : [];
      let combinedAudioStream: MediaStream | null = null;

      if (screenAudioTracks.length > 0 && micAudioTracks.length > 0) {
        const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const dest = audioCtx.createMediaStreamDestination();
        const screenSource = audioCtx.createMediaStreamSource(new MediaStream([screenAudioTracks[0]]));
        screenSource.connect(dest);
        const micSource = audioCtx.createMediaStreamSource(new MediaStream([micAudioTracks[0]]));
        micSource.connect(dest);
        combinedAudioStream = dest.stream;
        audioContextRef.current = audioCtx;
      } else if (micAudioTracks.length > 0) {
        combinedAudioStream = new MediaStream([micAudioTracks[0]]);
      } else if (screenAudioTracks.length > 0) {
        combinedAudioStream = new MediaStream([screenAudioTracks[0]]);
      }

      // ── Vídeo MUDO: apenas video track (sem audio embutido) ──
      const videoOnlyStream = new MediaStream([screenStream.getVideoTracks()[0]]);

      streamRef.current = screenStream;
      micStreamRef.current = micStream;

      // ── Session ID para chunked upload ──
      const sessionId = `rec_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
      recordingSessionRef.current = sessionId;
      videoChunkIndexRef.current = 0;
      audioChunkIndexRef.current = 0;
      chunkUploadQueueRef.current = [];
      console.log(`[ECAD] 🎬 Recording session started: ${sessionId}`);

      // ── Helper: upload chunk ao backend e liberar memória ──
      const uploadChunk = (blob: Blob, type: 'video' | 'audio', index: number) => {
        const formData = new FormData();
        formData.append('chunk', blob, `${type}_chunk_${index}.webm`);
        formData.append('sessionId', sessionId);
        formData.append('chunkIndex', String(index));
        formData.append('type', type);
        const headers: Record<string, string> = {};
        if (masterToken) headers['Authorization'] = `Bearer ${masterToken}`;
        // IMPORTANTE: remover Content-Type default (application/json) para axios auto-detectar multipart/form-data
        const promise = api.post('/ecad/recording-chunk', formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        })
          .then(() => {
            console.log(`[ECAD] 📦 ${type} chunk #${index} uploaded (${(blob.size / 1024).toFixed(0)}KB)`);
          })
          .catch(err => {
            console.error(`[ECAD] ❌ Falha upload ${type} chunk #${index}:`, err);
          });
        chunkUploadQueueRef.current.push(promise);
      };

      // ── Video Recorder (mudo, VP9, 1.5 Mbps, 15fps) ──
      const videoOptions: MediaRecorderOptions = {
        mimeType: 'video/webm;codecs=vp9',
        videoBitsPerSecond: REC_VIDEO_BITRATE,
      };
      let recorder: MediaRecorder;
      try {
        recorder = new MediaRecorder(videoOnlyStream, videoOptions);
      } catch (e) {
        // Fallback sem options avançadas
        try {
          recorder = new MediaRecorder(videoOnlyStream, { videoBitsPerSecond: REC_VIDEO_BITRATE });
        } catch {
          recorder = new MediaRecorder(videoOnlyStream);
        }
      }

      // ── Video chunks: upload imediato + manter blob para preview ──
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          recordingSizeBytesRef.current += e.data.size;
          const idx = videoChunkIndexRef.current++;
          uploadChunk(e.data, 'video', idx);
          // Manter blob para criar ObjectURL de preview (Opção A)
          videoChunkBlobsRef.current.push(e.data);
        }
      };

      // ── Audio Recorder separado (Opus, 64 kbps) ──
      if (combinedAudioStream) {
        const audioOptions: MediaRecorderOptions = {
          mimeType: 'audio/webm;codecs=opus',
          audioBitsPerSecond: REC_AUDIO_BITRATE,
        };
        let audioRecorder: MediaRecorder;
        try {
          audioRecorder = new MediaRecorder(combinedAudioStream, audioOptions);
        } catch (e) {
          audioRecorder = new MediaRecorder(combinedAudioStream);
        }

        // ── Audio chunks: upload imediato + liberar memória ──
        audioRecorder.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) {
            const idx = audioChunkIndexRef.current++;
            uploadChunk(e.data, 'audio', idx);
          }
        };
        audioRecorder.onstop = () => {
          console.log(`[ECAD] 🎙️ Audio recorder stopped. ${audioChunkIndexRef.current} chunks uploaded.`);
        };
        audioRecorderRef.current = audioRecorder;
      } else {
        audioRecorderRef.current = null;
        setAudioBlob(null);
      }

      // ── Video onstop: finalizar sessão no backend ──
      recorder.onstop = async () => {
        playBeep('stop');

        // Exit fullscreen when recording finishes
        try {
          if (document.fullscreenElement) {
            await document.exitFullscreen();
          }
        } catch (fErr) {
          console.warn('[ECAD] Failed to exit fullscreen mode:', fErr);
        }

        // Parar audio recorder
        if (audioRecorderRef.current && audioRecorderRef.current.state !== 'inactive') {
          audioRecorderRef.current.stop();
        }

        // Parar streams
        screenStream.getTracks().forEach(track => track.stop());
        if (micStream) micStream.getTracks().forEach(track => track.stop());
        if (audioContextRef.current) audioContextRef.current.close().catch(() => {});

        // ── Aguardar todos os uploads de chunks pendentes ──
        console.log(`[ECAD] ⏳ Aguardando ${chunkUploadQueueRef.current.length} chunks pendentes...`);
        await Promise.allSettled(chunkUploadQueueRef.current);
        chunkUploadQueueRef.current = [];

        // ── Opção A: Criar ObjectURL local para preview (instant, sem rede) ──
        let localPreviewUrl = '';
        try {
          const collectedBlobs = videoChunkBlobsRef.current;
          if (collectedBlobs.length > 0) {
            const fullBlob = new Blob(collectedBlobs, { type: 'video/webm' });
            localPreviewUrl = URL.createObjectURL(fullBlob);
            setVideoBlob(fullBlob);
            console.log(`[ECAD] ✅ ObjectURL criado para preview: ${(fullBlob.size / 1024 / 1024).toFixed(1)}MB`);
          }
        } catch (err) {
          console.warn('[ECAD] ⚠️ Falha ao criar ObjectURL local:', err);
        }
        // Limpar refs de chunks (blobs já foram fundidos em fullBlob)
        videoChunkBlobsRef.current = [];

        // Chamar finalize no backend — concatena chunks e retorna URLs
        try {
          const headers: Record<string, string> = {};
          if (masterToken) headers['Authorization'] = `Bearer ${masterToken}`;
          const res = await api.post('/ecad/finalize-recording', { sessionId }, { headers });
          if (res.data && res.data.ok) {
            const { videoUrl, audioUrl, videoSize, audioSize } = res.data;
            console.log(`[ECAD] ✅ Gravação finalizada — Vídeo: ${(videoSize / 1024 / 1024).toFixed(1)}MB, Áudio: ${(audioSize / 1024).toFixed(0)}KB`);
            const isLocalOrTest = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.includes('test.alunoconectado.com.br');

            // Resolvemos os caminhos locais de áudio/vídeo para o player do gravador local,
            // garantindo reprodução rápida sem bater no GCS de produção (evitando problemas de CORS/Range)
            const localVideoUrl = isLocalOrTest
              ? `/api/v1/ecad/serve-local/recordings/${sessionId}/original/video.webm`
              : (videoUrl.startsWith('http://') || videoUrl.startsWith('https://') ? videoUrl : videoUrl.replace(/^\/ecad\//, '/api/v1/ecad/serve-local/'));

            const localAudioUrl = isLocalOrTest
              ? `/api/v1/ecad/serve-local/recordings/${sessionId}/original/audio.webm`
              : (audioUrl.startsWith('http://') || audioUrl.startsWith('https://') ? audioUrl : audioUrl.replace(/^\/ecad\//, '/api/v1/ecad/serve-local/'));

            // Preview: Opção A (ObjectURL) > Opção B (serve-local)
            if (localPreviewUrl) {
              setPreviewUrl(localPreviewUrl);
            } else {
              setPreviewUrl(localVideoUrl);
              console.log('[ECAD] 🔄 Usando serve-local como fallback para preview:', localVideoUrl);
            }
            setAudioBlob(null); // Áudio no servidor
            setGcsVideoPath(videoUrl); // Para uso posterior no pipeline (GCS original)

            // Guardar URL do áudio para transcrição direta (sempre a versão local para evitar CORS/download lento no local)
            (window as any).__ecadLastAudioUrl = localAudioUrl;

            // ── Persistir sessão no sessionStorage para sobreviver a reload ──
            try {
              sessionStorage.setItem('ecad_session', JSON.stringify({
                sessionId,
                videoUrl: localVideoUrl,
                audioUrl: localAudioUrl,
                gcsVideoPath: videoUrl,
                timestamp: Date.now(),
              }));
              console.log('[ECAD] 💾 Sessão persistida no sessionStorage:', sessionId);
            } catch (e) {
              console.warn('[ECAD] Falha ao salvar sessão no sessionStorage:', e);
            }

            // ── Kanban Relay: enviar URL do vídeo de volta ao Kanban Trello ──
            try {
              const kanbanRelayUrl = kanbanRelayUrlRef.current;
              if (kanbanRelayUrl) {
                // 1. Auto-download do vídeo como backup local
                try {
                  const downloadUrl = localPreviewUrl || localVideoUrl;
                  if (downloadUrl) {
                    const a = document.createElement('a');
                    a.href = downloadUrl;
                    a.download = `qa-video-${sessionId}.webm`;
                    a.style.display = 'none';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    console.log('[ECAD] 💾 Auto-download do vídeo QA iniciado.');
                  }
                } catch (dlErr) {
                  console.warn('[ECAD] ⚠️ Falha no auto-download:', dlErr);
                }

                // 2. Enviar URL ao Kanban Relay — construir URL acessível
                let relayVideoUrl = videoUrl;
                if (!relayVideoUrl.startsWith('http')) {
                  if (relayVideoUrl.startsWith('/api/v1/ecad/serve-local/')) {
                    relayVideoUrl = `${window.location.origin}${relayVideoUrl}`;
                  } else {
                    const cleanPath = relayVideoUrl.replace(/^\/?(ecad\/)?/, '').replace(/^api\/v1\/ecad\/serve-local\//, '');
                    relayVideoUrl = `${window.location.origin}/api/v1/ecad/serve-local/${cleanPath}`;
                  }
                }
                const relayPayload = {
                  videoUrl: relayVideoUrl,
                  sessionId,
                };
                fetch(kanbanRelayUrl, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(relayPayload),
                }).then(r => {
                  if (r.ok) {
                    console.log('[ECAD] 📡 Vídeo URL enviado ao Kanban Relay com sucesso.');
                    // 3. Auto-fechar aba do recorder após 2s → volta ao Kanban
                    setTimeout(() => {
                      try { window.close(); } catch (e) { console.warn('[ECAD] Não foi possível fechar a aba automaticamente.'); }
                    }, 2000);
                  } else {
                    console.warn('[ECAD] ⚠️ Kanban Relay respondeu com status:', r.status);
                  }
                }).catch(err => {
                  console.warn('[ECAD] ⚠️ Falha ao enviar para Kanban Relay:', err.message);
                });
              }
            } catch (relayErr) {
              console.warn('[ECAD] ⚠️ Erro ao processar Kanban Relay:', relayErr);
            }
          } else {
            // Finalize falhou mas temos preview local
            if (localPreviewUrl) setPreviewUrl(localPreviewUrl);
            toast.error('Erro ao finalizar gravação no servidor.');
          }
        } catch (err: any) {
          console.error('[ECAD] ❌ Erro ao finalizar gravação:', err);
          // Ainda mostra preview local se disponível
          if (localPreviewUrl) setPreviewUrl(localPreviewUrl);
          toast.error(`Falha ao finalizar: ${err.message || 'erro interno'}`);
        }

        setStatus('completed');
        if (onStopCallback) onStopCallback();
      };

      screenStream.getVideoTracks()[0].onended = () => {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
          setStatus('idle');
          screenStream.getTracks().forEach(track => track.stop());
          if (micStream) micStream.getTracks().forEach(track => track.stop());
          if (audioContextRef.current) audioContextRef.current.close().catch(() => {});

          // Exit fullscreen if stopped during countdown
          try {
            if (document.fullscreenElement) {
              document.exitFullscreen().catch(() => {});
            }
          } catch (fErr) {}
          return;
        }
        if (recorderRef.current && recorderRef.current.state !== 'inactive') {
          recorderRef.current.stop();
        }
      };

      recorderRef.current = recorder;
      setStatus('countdown');
      setCountdownVal(3);
      playBeep('click');

      let val = 3;
      countdownIntervalRef.current = setInterval(() => {
        val -= 1;
        if (val > 0) {
          setCountdownVal(val);
          playBeep('click');
        } else {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
          setCountdownVal(0);

          recorder.start(REC_CHUNK_INTERVAL_MS);
          if (audioRecorderRef.current) {
            audioRecorderRef.current.start(REC_CHUNK_INTERVAL_MS);
          }
          setStatus('recording');
          setTimer(0);
          playBeep('start');
        }
      }, 1000);
    } catch (err: any) {
      console.error('Erro ao iniciar gravação:', err);
      setStatus('idle');
      setErrorMsg(err.message || 'Falha ao acessar os dispositivos de gravação.');
      toast.error('Erro ao acessar tela ou áudio. Verifique permissões.');

      // Ensure we exit fullscreen if we had somehow entered it
      try {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {});
        }
      } catch (fErr) {}
    }
  };

  const handlePause = () => {
    if (recorderRef.current && status === 'recording') {
      recorderRef.current.pause();
      if (audioRecorderRef.current && audioRecorderRef.current.state === 'recording') {
        audioRecorderRef.current.pause();
      }
      setStatus('paused');
      playBeep('pause');
    }
  };

  const handleResume = () => {
    if (recorderRef.current && status === 'paused') {
      recorderRef.current.resume();
      if (audioRecorderRef.current && audioRecorderRef.current.state === 'paused') {
        audioRecorderRef.current.resume();
      }
      setStatus('recording');
      playBeep('start');
    }
  };

  const handleStop = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
  };

  const recordTimelineEvent = (type: string, detail: string, label: string, meta?: any) => {
    timelineRef.current.push({
      time: timer,
      type,
      detail,
      label,
      meta: meta || null
    });
  };


  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          const base64Data = reader.result.split(',')[1];
          resolve(base64Data);
        } else {
          reject(new Error('Falha ao converter blob para base64'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const uploadOriginalVideoIfNeeded = async (): Promise<string> => {
    if (gcsVideoPath) return gcsVideoPath;
    if (!videoBlob) throw new Error('Nenhum vídeo gravado disponível.');

    const ext = 'webm';
    const cleanName = `gravacao_${Date.now()}_original.${ext}`;
    const headers = masterToken ? { Authorization: `Bearer ${masterToken}` } : {};

    try {
      // 1. Tentar obter Signed URL do backend para upload direto ao GCS
      const resUrl = await api.post('/ecad/gerar-upload-url', {
        filename: cleanName,
        contentType: 'video/webm',
        tipo: 'VIDEO'
      }, { headers });

      if (resUrl.data && resUrl.data.ok && resUrl.data.uploadUrl) {
        const { uploadUrl, gcsPath } = resUrl.data;
        
        // Fazer PUT request direto para o GCS
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: videoBlob,
          headers: {
            'Content-Type': 'video/webm',
          }
        });

        if (!uploadResponse.ok) {
          throw new Error(`Falha no upload direto para o Storage: ${uploadResponse.statusText}`);
        }

        setGcsVideoPath(gcsPath);
        return gcsPath;
      }
    } catch (err: any) {
      console.warn('Falha no upload direto para o GCS. Fazendo fallback para upload local via API:', err);
    }

    // 2. Fallback local: Upload clássico via multipart/form-data
    const file = new File([videoBlob], cleanName, { type: 'video/webm' });
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tipo', 'VIDEO');

    const multipartHeaders = {
      'Content-Type': 'multipart/form-data',
      ...headers
    };

    const uploadRes = await api.post('/ecad/upload', formData, { headers: multipartHeaders });
    if (uploadRes.data && uploadRes.data.ok) {
      const path = uploadRes.data.gcsPath;
      setGcsVideoPath(path);
      return path;
    } else {
      throw new Error('Falha no upload do vídeo original.');
    }
  };

  const handleTranscribe = async (onEmpty?: () => void) => {
    // ── Guard: prevent double transcription ──
    if (isTranscribing) {
      console.warn('[ECAD] handleTranscribe bloqueado — já transcrevendo');
      return;
    }

    // ── Prioridade: áudio do servidor (chunked) > audioBlob > videoBlob ──
    const serverAudioUrl = (window as any).__ecadLastAudioUrl as string | undefined;
    const blobToUse = audioBlob || videoBlob;
    
    if (!serverAudioUrl && !blobToUse) {
      toast.error('Nenhuma gravação disponível para transcrever.');
      return;
    }

    setIsTranscribing(true);
    toast.loading('Processando áudio... Analisando trechos no servidor.', { id: 'transcribe-audio' });

    try {
      const headers: Record<string, string> = {};
      if (masterToken) headers['Authorization'] = `Bearer ${masterToken}`;
      let res: any;

      if (serverAudioUrl) {
        // ── Áudio já está no servidor — enviar URL como FormData (FileInterceptor exige multipart) ──
        console.log('[ECAD] Transcrevendo via URL do servidor:', serverAudioUrl);
        const formData = new FormData();
        formData.append('audioUrl', serverAudioUrl);
        formData.append('mimeType', 'audio/webm');
        res = await api.post('/ecad/transcrever-audio', formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
      } else if (blobToUse) {
        // ── Fallback: enviar blob via FormData ──
        const formData = new FormData();
        formData.append('audio', blobToUse, 'recording.webm');
        formData.append('mimeType', audioBlob ? 'audio/webm' : 'video/webm');
        res = await api.post('/ecad/transcrever-audio', formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
      }

      if (res?.data?.ok) {
        if (!res.data.text || !res.data.text.trim()) {
          if (!useMic) {
            toast.error("Nenhuma fala detectada. Nota: O microfone estava desativado. Clique no ícone de microfone (🔇) no topo para ativá-lo antes de gravar.", { id: 'transcribe-audio', duration: 7000 });
          } else {
            toast.error("Nenhuma fala detectada no vídeo. Certifique-se de que falou claramente e o microfone estava ativo.", { id: 'transcribe-audio' });
          }
          setIsTranscribing(false);
          if (onEmpty) onEmpty();
          return;
        }
        setVideoScript(res.data.text);
        if (res.data.vttUrl) {
          setLegendaUrl(res.data.vttUrl);
        }
        if (res.data.segments) {
          const mapped = res.data.segments.map((seg: any) => ({
            ...seg,
            originalText: seg.originalText || seg.text
          }));
          setTranscriptionSegments(mapped);
          // ── Salvar original imutável ──
          setOriginalSegments(JSON.parse(JSON.stringify(mapped)));
          setOriginalVideoScript(res.data.text);
          setRefinementVersions([]);
          setActiveVersionIndex(-1);

          // ── Persistir transcrição no sessionStorage ──
          try {
            const saved = sessionStorage.getItem('ecad_session');
            if (saved) {
              const session = JSON.parse(saved);
              session.segments = mapped;
              session.videoScript = res.data.text;
              session.legendaUrl = res.data.vttUrl || '';
              sessionStorage.setItem('ecad_session', JSON.stringify(session));
              console.log('[ECAD] 💾 Transcrição persistida no sessionStorage');
            }
          } catch (e) {
            console.warn('[ECAD] Falha ao persistir transcrição:', e);
          }
        }
        setAudioBlob(null);
        toast.success('Áudio transcrito com sucesso!', { id: 'transcribe-audio' });
      } else {
        const errMsg = res?.data?.error?.message || 'Erro ao transcrever áudio.';
        toast.error(errMsg, { id: 'transcribe-audio', duration: 8000 });
      }
    } catch (err: any) {
      console.error('Erro na transcrição:', err);
      const errMsg = extractApiErrorMessage(err, err.message || 'erro interno');
      toast.error(`Falha ao transcrever: ${errMsg}`, { id: 'transcribe-audio', duration: 8000 });
    } finally {
      setIsTranscribing(false);
    }
  };

  // Debounce ref for VTT backend updates (avoid POST per keystroke)
  const updateSegmentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleUpdateSegment = async (idx: number, newText: string) => {
    const updated = transcriptionSegments.map(seg => {
      if (seg.index === idx) {
        return { ...seg, text: newText };
      }
      return seg;
    });
    setTranscriptionSegments(updated);
    
    const consolidatedText = updated.map(s => s.text).join(' ');
    setVideoScript(consolidatedText);

    // Debounce VTT backend update (500ms)
    if (updateSegmentTimerRef.current) {
      clearTimeout(updateSegmentTimerRef.current);
    }

    if (legendaUrl) {
      updateSegmentTimerRef.current = setTimeout(async () => {
        try {
          const headers = masterToken ? { Authorization: `Bearer ${masterToken}` } : {};
          const res = await api.post('/ecad/atualizar-legenda', {
            vttUrl: legendaUrl,
            segments: updated
          }, { headers });
          if (res.data && res.data.ok) {
            setLegendaUrl('');
            setTimeout(() => {
              setLegendaUrl(res.data.vttUrl);
            }, 50);
          }
        } catch (err) {
          console.error('Erro ao atualizar legenda VTT:', err);
        }
      }, 500);
    }
  };

  const handleRefine = async () => {
    // ── Guard: prevent double refinement ──
    if (isRefining) {
      console.warn('[ECAD] handleRefine bloqueado — já refinando');
      return;
    }
    if (!videoScript) {
      toast.error('Nenhum roteiro disponível para aprimorar.');
      return;
    }
    setIsRefining(true);
    const versionNum = refinementVersions.length + 1;
    toast.loading(`Gerando aprimoramento v${versionNum} com IA...`, { id: 'refine-script' });

    try {
      const headers = masterToken ? { Authorization: `Bearer ${masterToken}` } : {};
      // Sempre enviar os segmentos ORIGINAIS para o Gemini refinar do zero
      const segsToRefine = originalSegments.length > 0 ? originalSegments : (
        transcriptionSegments.length > 0 ? transcriptionSegments : undefined
      );
      const textToRefine = originalVideoScript || videoScript;

      const res = await api.post('/ecad/aprimorar-roteiro', { 
        text: textToRefine,
        segments: segsToRefine,
        sessionId: recordingSessionRef.current || undefined,
      }, { headers });

      if (res.data && res.data.ok) {
        // ── Criar nova versão ──
        const newVersion: RefinementVersion = {
          label: `Aprimorado v${versionNum}`,
          segments: res.data.segments ? [...res.data.segments] : [],
          timestamp: Date.now(),
          analysis: res.data.analysis || undefined,
        };
        const updatedVersions = [...refinementVersions, newVersion];
        setRefinementVersions(updatedVersions);
        
        // Ativar a nova versão
        const newIdx = updatedVersions.length - 1;
        setActiveVersionIndex(newIdx);
        setVideoScript(res.data.refined);
        if (res.data.segments) {
          setTranscriptionSegments(res.data.segments);
        }
        toast.success(`Aprimoramento v${versionNum} gerado!`, { id: 'refine-script' });
      } else {
        toast.error('Erro ao aprimorar roteiro.', { id: 'refine-script' });
      }
    } catch (err: any) {
      console.error('Erro no aprimoramento:', err);
      toast.error(`Falha ao aprimorar: ${err.message || 'erro interno'}`, { id: 'refine-script' });
    } finally {
      setIsRefining(false);
    }
  };

  // ── Seleção de versão ──
  const handleSelectVersion = (versionIndex: number) => {
    if (versionIndex === -1) {
      // Restaurar original
      setActiveVersionIndex(-1);
      setTranscriptionSegments([...originalSegments]);
      setVideoScript(originalVideoScript);
    } else if (versionIndex >= 0 && versionIndex < refinementVersions.length) {
      const version = refinementVersions[versionIndex];
      setActiveVersionIndex(versionIndex);
      if (version.segments.length > 0) {
        setTranscriptionSegments([...version.segments]);
        setVideoScript(version.segments.map(s => s.text).join(' '));
      }
    }
  };

  const handleRestoreOriginal = () => {
    handleSelectVersion(-1);
    toast.success('Texto original restaurado.');
  };

  const handleSynthesize = async (subtopicoId?: string, force = false, modelId?: string) => {
    // ── Guard: prevent double synthesis ──
    if (isSynthesizing) {
      console.warn('[ECAD] handleSynthesize bloqueado — já sintetizando');
      return false;
    }
    if (!videoScript) {
      toast.error('Por favor, defina o roteiro textual para sintetizar.');
      return false;
    }

    setIsSynthesizing(true);
    toast.loading('Gerando voz sintética e mixando com o vídeo...', { id: 'synthesize-audio' });

    try {
      const videoPath = await uploadOriginalVideoIfNeeded();
      const headers = masterToken ? { Authorization: `Bearer ${masterToken}` } : {};
      
      const res = await api.post('/ecad/sintetizar-manual', {
        text: videoScript,
        subtopicoId: subtopicoId || undefined,
        originalVideoPath: videoPath,
        voice: voiceName,
        modelId: modelId || undefined,
        segments: transcriptionSegments.length > 0 ? transcriptionSegments : undefined,
        force: force,
        sessionId: recordingSessionRef.current || undefined,
      }, { headers });

      if (res.data && res.data.ok) {
        const newVideoUrl = res.data.videoUrl;
        setProcessedVideoUrl(newVideoUrl);
        if (res.data.vttUrl) {
          setLegendaUrl(res.data.vttUrl);
        }
        toast.success('Nova voz mixada ao vídeo com sucesso! Reproduza o preview para ouvir.', { id: 'synthesize-audio' });
        return true;
      } else {
        toast.error('Erro ao sintetizar voz.', { id: 'synthesize-audio' });
        return false;
      }
    } catch (err: any) {
      console.error('Erro na síntese:', err);
      toast.error(`Falha ao sintetizar: ${err.message || 'erro interno'}`, { id: 'synthesize-audio' });
      return false;
    } finally {
      setIsSynthesizing(false);
    }
  };

  const handleTestVoice = async () => {
    // ── Guard: prevent double voice test ──
    if (isTestingVoice) {
      console.warn('[ECAD] handleTestVoice bloqueado — já testando voz');
      return;
    }
    // Cleanup: stop previous test audio if playing
    if (testVoiceAudioRef.current) {
      testVoiceAudioRef.current.pause();
      testVoiceAudioRef.current = null;
    }
    setIsTestingVoice(true);
    toast.loading('Gerando amostra de voz...', { id: 'test-voice' });
    try {
      const headers = masterToken ? { Authorization: `Bearer ${masterToken}` } : {};
      const frase = `Olá! Esta é uma demonstração de áudio para validar a voz selecionada no Aluno Conectado.`;
      
      const res = await api.post('/ecad/sintetizar', {
        text: frase,
        voice: voiceName,
        force: true
      }, { headers });

      if (res.data && res.data.ok && res.data.audioUrl) {
        toast.success('Tocando amostra de voz...', { id: 'test-voice' });
        const audio = new Audio(res.data.audioUrl);
        testVoiceAudioRef.current = audio;
        audio.onended = () => { testVoiceAudioRef.current = null; };
        await audio.play();
      } else {
        toast.error('Erro ao gerar amostra de voz.', { id: 'test-voice' });
      }
    } catch (err: any) {
      console.error('Erro ao testar voz:', err);
      toast.error(`Falha ao testar voz: ${err.message || 'erro interno'}`, { id: 'test-voice' });
    } finally {
      setIsTestingVoice(false);
    }
  };

  const handleSave = async (
    vincular: boolean, 
    linkInfo?: { 
      selectedPerfilId: string; 
      selectedTopicoId: string; 
      selectedSubtopicoId: string; 
      activeTopico: any;
      activeVideoUrl?: string;
      activeVttUrl?: string;
    }
  ) => {
    if (!videoBlob && !processedVideoUrl && !linkInfo?.activeVideoUrl) return false;
    
    if (vincular && (!linkInfo?.selectedPerfilId || !linkInfo?.selectedTopicoId || !linkInfo?.selectedSubtopicoId)) {
      toast.error('Selecione o perfil, módulo e aula para vincular o vídeo.');
      return false;
    }

    setUploading(true);
    toast.loading((linkInfo?.activeVideoUrl || processedVideoUrl) ? 'Vinculando vídeo processado...' : 'Enviando vídeo para o Firebase Storage...', { id: 'upload-gravacao' });

    try {
      let finalUrl = linkInfo?.activeVideoUrl || processedVideoUrl;

      if (!finalUrl && videoBlob) {
        const ext = 'webm';
        const cleanName = `gravacao_${Date.now()}.${ext}`;
        const headers = masterToken ? { Authorization: `Bearer ${masterToken}` } : {};

        try {
          // 1. Tentar upload direto no GCS via Signed URL
          const resUrl = await api.post('/ecad/gerar-upload-url', {
            filename: cleanName,
            contentType: 'video/webm',
            tipo: 'VIDEO'
          }, { headers });

          if (resUrl.data && resUrl.data.ok && resUrl.data.uploadUrl) {
            const { uploadUrl, url } = resUrl.data;

            // PUT request direto para o GCS
            const uploadResponse = await fetch(uploadUrl, {
              method: 'PUT',
              body: videoBlob,
              headers: {
                'Content-Type': 'video/webm',
              }
            });

            if (!uploadResponse.ok) {
              throw new Error(`Falha no upload direto para o Storage: ${uploadResponse.statusText}`);
            }

            finalUrl = url;
          }
        } catch (err: any) {
          console.warn('Falha no upload direto para o GCS. Fazendo fallback para upload local via API:', err);
        }

        // 2. Fallback local via Multipart FormData
        if (!finalUrl) {
          const file = new File([videoBlob], cleanName, { type: 'video/webm' });
          const formData = new FormData();
          formData.append('file', file);
          formData.append('tipo', 'VIDEO');

          const multipartHeaders = {
            'Content-Type': 'multipart/form-data',
            ...headers
          };
          
          const uploadRes = await api.post('/ecad/upload', formData, { headers: multipartHeaders });
          if (uploadRes.data && uploadRes.data.ok) {
            finalUrl = uploadRes.data.url;
          } else {
            throw new Error('Erro ao fazer upload do vídeo gravado.');
          }
        }
      }

      if (finalUrl) {
        if (vincular && linkInfo) {
          const { selectedTopicoId, selectedSubtopicoId, activeTopico } = linkInfo;
          const targetSub = activeTopico?.subtopicos.find((s: any) => s.id === selectedSubtopicoId);
          if (!targetSub) throw new Error('Aula não encontrada no catálogo.');

          await api.post('/ecad/roteiro', {
            id: targetSub.id,
            topicoId: selectedTopicoId,
            titulo: targetSub.titulo,
            descricao: targetSub.descricao || '',
            ordem: targetSub.ordem,
            duracao: targetSub.duracao || '2m 00s',
            dificuldade: targetSub.dificuldade || 'Básico',
            status: targetSub.status,
            videoUrl: finalUrl,
            pdfUrl: targetSub.pdfUrl,
            legendaUrl: linkInfo?.activeVttUrl || legendaUrl || null,
            roteiroEventos: timelineRef.current.length > 0 ? timelineRef.current : null,
            roteiroTexto: videoScript || null,
            sessionId: recordingSessionRef.current || undefined,
            config: {
              ...(targetSub.config || {}),
              originalVideoUrl: gcsVideoPath || null
            }
          }, {
            headers: masterToken ? { Authorization: `Bearer ${masterToken}` } : {}
          });

          toast.success('Vídeo gravado e vinculado à aula com sucesso!', { id: 'upload-gravacao' });
        } else {
          toast.success(`Vídeo salvo com sucesso! URL: ${finalUrl}`, { id: 'upload-gravacao' });
        }
        
        setStatus('idle');
        return true;
      }
      return false;
    } catch (err: any) {
      console.error('Erro ao salvar vídeo', err);
      toast.error(`Falha ao salvar a gravação: ${err.message || 'erro desconhecido'}`, { id: 'upload-gravacao' });
      return false;
    } finally {
      setUploading(false);
    }
  };

  const handleDiscard = async () => {
    if (uploading) return false;
    const confirmDiscard = window.confirm('Deseja realmente descartar a gravação atual? O material gravado será perdido.');
    if (confirmDiscard) {
      const pathsToDelete = [gcsVideoPath, processedVideoUrl, legendaUrl].filter(Boolean);
      if (pathsToDelete.length > 0) {
        try {
          const headers = masterToken ? { Authorization: `Bearer ${masterToken}` } : {};
          await api.post('/ecad/delete-videos', { videoPaths: pathsToDelete }, { headers });
        } catch (err) {
          console.warn('Erro ao limpar arquivos descartados do servidor:', err);
        }
      }

      setVideoBlob(null);
      setAudioBlob(null);
      setPreviewUrl('');
      setTimer(0);
      setStatus('idle');
      setVideoScript('');
      setLegendaUrl('');
      setTranscriptionSegments([]);
      setGcsVideoPath('');
      setProcessedVideoUrl('');
      timelineRef.current = [];
      toast.info('Gravação descartada.');
      return true;
    }
    return false;
  };

  return {
    status,
    setStatus,
    useMic,
    setUseMic,
    timer,
    setTimer,
    errorMsg,
    setErrorMsg,
    countdownVal,
    setCountdownVal,
    videoBlob,
    audioBlob,
    previewUrl,
    uploading,
    videoScript,
    setVideoScript,
    legendaUrl,
    setLegendaUrl,
    gcsVideoPath,
    processedVideoUrl,
    isTranscribing,
    isRefining,
    isSynthesizing,
    voiceName,
    setVoiceName,
    isTestingVoice,
    transcriptionSegments,
    setTranscriptionSegments,
    originalSegments,
    setOriginalSegments,
    originalVideoScript,
    refinementVersions,
    activeVersionIndex,
    timelineRef,
    formatTime,
    playBeep,
    handleStart,
    handlePause,
    handleResume,
    handleStop,
    recordTimelineEvent,
    handleTranscribe,
    handleUpdateSegment,
    handleRefine,
    handleSelectVersion,
    handleRestoreOriginal,
    handleSynthesize,
    handleTestVoice,
    handleSave,
    handleDiscard,
    recordingSizeBytesRef,
    recordingSessionRef,
  };
}
